import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Alert,
  CircularProgress,
  Link,
  Divider,
  InputAdornment,
  IconButton,
  Card,
  CardContent,
  Avatar,
  Chip,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  Person as PersonIcon,
  Clear as ClearIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import airtableService from '../../services/airtable';
import { User, Company } from '../../types';

const Login: React.FC = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isRegistering, setIsRegistering] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [users, setUsers] = useState<User[]>([]);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [loadingUsers, setLoadingUsers] = useState(false);
  // Company selection state
  const [companies, setCompanies] = useState<Company[]>([]);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [companyPasscode, setCompanyPasscode] = useState('');
  const [loadingCompanies, setLoadingCompanies] = useState(false);
  const [showCompanySelection, setShowCompanySelection] = useState(false);
  const { login, register, loading } = useAuth();
  const navigate = useNavigate();

  // Format phone number consistently
  const formatPhoneNumber = (phone: string): string => {
    // Remove all non-digit characters
    const cleaned = phone.replace(/\D/g, '');

    // Format as (###) ###-####
    if (cleaned.length === 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    }

    // Return original if not 10 digits
    return phone;
  };

  // Fetch users and companies from Airtable when component mounts
  useEffect(() => {
    const fetchUsers = async () => {
      setLoadingUsers(true);
      try {
        console.log('👥 Fetching users...');
        const response = await airtableService.getUsers();
        if (response.success) {
          // Filter only active users — treat undefined Active Status as active
          const activeUsers = response.data.filter(user => user.active !== false);
          console.log('✅ Active users loaded:', activeUsers.length, 'users');
          console.log('👤 User details with userGroupType:');
          activeUsers.forEach((u, index) => {
            console.log(`  ${index + 1}. ${u.firstName} ${u.lastName}:`, {
              userType: u.userType,
              userGroupType: u.userGroupType,
              hasUserGroupType: !!u.userGroupType
            });
          });
          setUsers(activeUsers);
        } else {
          setError('Failed to load user list');
        }
      } catch (err) {
        setError('Failed to load user list');
        console.error('Error fetching users:', err);
      } finally {
        setLoadingUsers(false);
      }
    };

    const fetchVisibleCompanies = async () => {
      try {
        setLoadingCompanies(true);
        console.log('🏢 Fetching visible companies...');
        const response = await airtableService.getVisibleCompanies();
        if (response.success) {
          console.log('✅ Visible companies loaded:', response.data.length, 'companies');
          console.log('🏢 Company details:');
          response.data.forEach((c, index) => {
            console.log(`  ${index + 1}. ${c.name}:`, {
              status: c.status,
              visibleInUI: c.visibleInUI,
              hasPasscode: !!c.companyPasscode
            });
          });
          setCompanies(response.data);
        } else {
          console.error('❌ Failed to load companies:', response.error);
        }
      } catch (error) {
        console.error('💥 Error fetching companies:', error);
      } finally {
        setLoadingCompanies(false);
      }
    };

    fetchUsers();
    fetchVisibleCompanies();
  }, []);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    try {
      console.log('🔐 Login attempt:', { email, passwordLength: password.length });

      if (isRegistering) {
        console.log('📝 Registration attempt:', { email, firstName, lastName });
        await register({
          email,
          firstName,
          lastName,
          userType: 'Candidate', // Default to candidate, can be changed by admin later
        });
      } else {
        // Ensure user is selected and password is provided
        if (!selectedUser) {
          setError('Please select a user account');
          return;
        }
        if (!password) {
          setError('Please enter your password');
          return;
        }

        // Check company access for Company Users
        if (selectedUser.userGroupType === 'Company User') {
          if (!selectedCompany) {
            setError('Please select a company');
            return;
          }

          const isPasscodeValid = await handleCompanyPasscodeVerification();
          if (!isPasscodeValid) {
            return;
          }
        }

        console.log('🔑 Attempting login with:', email);
        await login(email, password, selectedCompany?.id);
        console.log('✅ Login successful, redirecting to dashboard');
      }
      navigate('/dashboard');
    } catch (err) {
      console.error('❌ Login error:', err);
      setError(err instanceof Error ? err.message : 'An error occurred');
    }
  };

  const handleUserSelect = (user: User) => {
    setSelectedUser(user);
    setEmail(user.email);
    setPassword(''); // Always require password entry
    setError('');

    console.log('🔍 User selected:', {
      name: `${user.firstName} ${user.lastName}`,
      email: user.email,
      userType: user.userType,
      userGroupType: user.userGroupType,
      companyId: user.companyId
    });

    // Check if user requires company selection
    if (user.userGroupType === 'Company User') {
      console.log('✅ Company User detected - showing company selection');
      setShowCompanySelection(true);
    } else {
      console.log('👑 Administrator User detected - bypassing company selection');
      setShowCompanySelection(false);
      setSelectedCompany(null);
      setCompanyPasscode('');
    }
  };

  const handleCompanySelect = (company: Company) => {
    setSelectedCompany(company);
    setCompanyPasscode('');
    setError('');
  };

  const handleCompanyPasscodeVerification = async () => {
    if (!selectedCompany || !companyPasscode.trim()) {
      setError('Please enter the company passcode');
      return false;
    }

    try {
      const response = await airtableService.verifyCompanyPasscode(selectedCompany.id, companyPasscode);
      if (response.success && response.data) {
        return true;
      } else {
        setError('Invalid company passcode');
        return false;
      }
    } catch (err) {
      setError('Failed to verify company passcode');
      return false;
    }
  };

  const handleClearSelection = () => {
    setSelectedUser(null);
    setEmail('');
    setPassword('');
    setError('');
    setShowCompanySelection(false);
    setSelectedCompany(null);
    setCompanyPasscode('');
  };

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  return (
    <Container component="main" maxWidth="sm">
      <Box
        sx={{
          marginTop: 8,
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          minHeight: 'calc(100vh - 100px)',
          justifyContent: 'center',
        }}
      >
        <Paper
          elevation={0}
          className="glass-card glass-card-hover"
          sx={{
            padding: 4,
            width: '100%',
            background: 'linear-gradient(145deg, rgba(30, 41, 59, 0.9), rgba(51, 65, 85, 0.8))',
            backdropFilter: 'blur(20px)',
            border: '1px solid rgba(59, 130, 246, 0.3)',
            borderRadius: '16px',
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4), 0 4px 8px rgba(59, 130, 246, 0.15)',
          }}
        >
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Typography
              component="h1"
              variant="h3"
              className="title-gradient"
              sx={{
                mb: 1,
                fontWeight: 800,
                letterSpacing: '-0.02em',
                background: 'linear-gradient(135deg, #8AA6F9 0%, #F7B808 50%, #ffffff 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                textShadow: '0 4px 8px rgba(0, 0, 0, 0.3)',
              }}
            >
              M4 Consulting ATS
            </Typography>
            <Box
              sx={{
                width: '60px',
                height: '4px',
                background: 'linear-gradient(90deg, #F7B808, #8AA6F9)',
                margin: '0 auto',
                borderRadius: '2px',
                mb: 2,
              }}
            />
          </Box>
          <Typography
            variant="h5"
            align="center"
            gutterBottom
            sx={{
              color: 'rgba(255, 255, 255, 0.9)',
              fontWeight: 600,
              mb: 3,
            }}
          >
            {isRegistering ? 'Create Account' : 'Sign In'}
          </Typography>

          {error && (
            <Alert
              severity="error"
              sx={{
                mb: 2,
                backgroundColor: 'rgba(239, 68, 68, 0.1)',
                border: '1px solid rgba(239, 68, 68, 0.3)',
                borderRadius: '8px',
                '& .MuiAlert-icon': {
                  color: '#f87171',
                },
                '& .MuiAlert-message': {
                  color: '#fecaca',
                },
              }}
            >
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 1 }}>
            {isRegistering && (
              <>
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="firstName"
                  label="First Name"
                  name="firstName"
                  autoComplete="given-name"
                  autoFocus
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  className="professional-input"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: 'rgba(30, 41, 59, 0.6)',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                      borderRadius: '8px',
                      backdropFilter: 'blur(10px)',
                      transition: 'all 0.3s ease',
                      '& fieldset': {
                        border: 'none',
                      },
                      '&:hover': {
                        borderColor: 'rgba(59, 130, 246, 0.5)',
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.1)',
                      },
                      '&.Mui-focused': {
                        borderColor: 'rgba(59, 130, 246, 0.8)',
                        boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.2)',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&.Mui-focused': {
                        color: '#60a5fa',
                      },
                    },
                    '& .MuiOutlinedInput-input': {
                      color: 'white',
                    },
                  }}
                />
                <TextField
                  margin="normal"
                  required
                  fullWidth
                  id="lastName"
                  label="Last Name"
                  name="lastName"
                  autoComplete="family-name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  className="professional-input"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      background: 'rgba(30, 41, 59, 0.6)',
                      border: '1px solid rgba(59, 130, 246, 0.3)',
                      borderRadius: '8px',
                      backdropFilter: 'blur(10px)',
                      transition: 'all 0.3s ease',
                      '& fieldset': {
                        border: 'none',
                      },
                      '&:hover': {
                        borderColor: 'rgba(59, 130, 246, 0.5)',
                        boxShadow: '0 4px 12px rgba(59, 130, 246, 0.1)',
                      },
                      '&.Mui-focused': {
                        borderColor: 'rgba(59, 130, 246, 0.8)',
                        boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.2)',
                      },
                    },
                    '& .MuiInputLabel-root': {
                      color: 'rgba(255, 255, 255, 0.7)',
                      '&.Mui-focused': {
                        color: '#60a5fa',
                      },
                    },
                    '& .MuiOutlinedInput-input': {
                      color: 'white',
                    },
                  }}
                />
              </>
            )}

            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label={selectedUser ? `Selected User: ${selectedUser.firstName} ${selectedUser.lastName}` : "Email Address"}
              name="email"
              autoComplete="email"
              autoFocus={!isRegistering && !selectedUser}
              value={email}
              onChange={(e) => !selectedUser && setEmail(e.target.value)}
              InputProps={{
                readOnly: !!selectedUser,
                startAdornment: selectedUser ? (
                  <InputAdornment position="start">
                    <Avatar
                      sx={{
                        width: 24,
                        height: 24,
                        fontSize: '0.7rem',
                        mr: 1,
                        background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)'
                      }}
                    >
                      {selectedUser.firstName?.[0]}{selectedUser.lastName?.[0]}
                    </Avatar>
                  </InputAdornment>
                ) : undefined,
                endAdornment: selectedUser ? (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="clear user selection"
                      onClick={handleClearSelection}
                      edge="end"
                      sx={{
                        color: 'rgba(255, 255, 255, 0.7)',
                        '&:hover': {
                          color: '#60a5fa',
                        },
                      }}
                    >
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ) : undefined,
              }}
              className="professional-input"
              sx={{
                '& .MuiOutlinedInput-root': {
                  background: 'rgba(30, 41, 59, 0.6)',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  borderRadius: '8px',
                  backdropFilter: 'blur(10px)',
                  transition: 'all 0.3s ease',
                  '& fieldset': {
                    border: 'none',
                  },
                  '&:hover': {
                    borderColor: 'rgba(59, 130, 246, 0.5)',
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.1)',
                  },
                  '&.Mui-focused': {
                    borderColor: 'rgba(59, 130, 246, 0.8)',
                    boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.2)',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&.Mui-focused': {
                    color: '#60a5fa',
                  },
                },
                '& .MuiOutlinedInput-input': {
                  color: 'white',
                },
              }}
            />
            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete={isRegistering ? 'new-password' : 'current-password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="professional-input"
              sx={{
                '& .MuiOutlinedInput-root': {
                  background: 'rgba(30, 41, 59, 0.6)',
                  border: '1px solid rgba(59, 130, 246, 0.3)',
                  borderRadius: '8px',
                  backdropFilter: 'blur(10px)',
                  transition: 'all 0.3s ease',
                  '& fieldset': {
                    border: 'none',
                  },
                  '&:hover': {
                    borderColor: 'rgba(59, 130, 246, 0.5)',
                    boxShadow: '0 4px 12px rgba(59, 130, 246, 0.1)',
                  },
                  '&.Mui-focused': {
                    borderColor: 'rgba(59, 130, 246, 0.8)',
                    boxShadow: '0 0 0 2px rgba(59, 130, 246, 0.2)',
                  },
                },
                '& .MuiInputLabel-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&.Mui-focused': {
                    color: '#60a5fa',
                  },
                },
                '& .MuiOutlinedInput-input': {
                  color: 'white',
                },
                '& .MuiIconButton-root': {
                  color: 'rgba(255, 255, 255, 0.7)',
                  '&:hover': {
                    color: '#60a5fa',
                  },
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      aria-label="toggle password visibility"
                      onClick={handleTogglePasswordVisibility}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Button
              type="submit"
              fullWidth
              variant="contained"
              className="gradient-button-primary"
              sx={{
                mt: 3,
                mb: 2,
                py: 1.5,
                background: 'linear-gradient(135deg, #2563eb 0%, #3b82f6 50%, #1d4ed8 100%)',
                borderRadius: '8px',
                fontWeight: 600,
                fontSize: '1rem',
                textTransform: 'none',
                boxShadow: '0 4px 12px rgba(37, 99, 235, 0.4)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-1px)',
                  boxShadow: '0 6px 16px rgba(37, 99, 235, 0.5)',
                  background: 'linear-gradient(135deg, #1d4ed8 0%, #2563eb 50%, #3b82f6 100%)',
                },
                '&:active': {
                  transform: 'translateY(0)',
                },
                '&:disabled': {
                  background: 'rgba(59, 130, 246, 0.3)',
                  color: 'rgba(255, 255, 255, 0.5)',
                  transform: 'none',
                  boxShadow: 'none',
                },
              }}
              disabled={loading}
            >
              {loading ? (
                <CircularProgress
                  size={24}
                  sx={{ color: 'rgba(255, 255, 255, 0.8)' }}
                />
              ) : (
                isRegistering ? 'Create Account' : 'Sign In'
              )}
            </Button>

            <Box textAlign="center">
              <Link
                component="button"
                variant="body2"
                sx={{
                  color: '#60a5fa',
                  textDecoration: 'none',
                  fontWeight: 500,
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    color: '#93c5fd',
                    textDecoration: 'underline',
                  },
                }}
                onClick={(e) => {
                  e.preventDefault();
                  setIsRegistering(!isRegistering);
                  setError('');
                }}
              >
                {isRegistering
                  ? "Already have an account? Sign in"
                  : "Don't have an account? Sign up"}
              </Link>
            </Box>
          </Box>

          {!isRegistering && (
            <>
              <Divider
                sx={{
                  my: 3,
                  '&::before, &::after': {
                    borderColor: 'rgba(59, 130, 246, 0.2)',
                  },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    fontWeight: 500,
                    px: 2,
                  }}
                >
                  Select User Account
                </Typography>
              </Divider>

              {loadingUsers ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={32} sx={{ color: '#60a5fa' }} />
                </Box>
              ) : (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxHeight: '300px', overflowY: 'auto' }}>
                  {users.map((user) => (
                    <Card
                      key={user.id}
                      sx={{
                        background: selectedUser?.id === user.id
                          ? 'linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(59, 130, 246, 0.1))'
                          : 'linear-gradient(135deg, rgba(30, 41, 59, 0.8), rgba(51, 65, 85, 0.6))',
                        border: selectedUser?.id === user.id
                          ? '2px solid rgba(59, 130, 246, 0.5)'
                          : '1px solid rgba(59, 130, 246, 0.2)',
                        borderRadius: '12px',
                        cursor: 'pointer',
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          border: '1px solid rgba(59, 130, 246, 0.4)',
                          transform: 'translateY(-1px)',
                          boxShadow: '0 4px 12px rgba(59, 130, 246, 0.15)',
                        },
                      }}
                      onClick={() => handleUserSelect(user)}
                    >
                      <CardContent sx={{ py: 1, px: 2, '&:last-child': { pb: 1 } }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar
                            sx={{
                              width: 40,
                              height: 40,
                              background: 'linear-gradient(135deg, #3b82f6 0%, #60a5fa 100%)',
                              fontSize: '1rem',
                              fontWeight: 600
                            }}
                          >
                            {user.firstName?.[0]}{user.lastName?.[0]}
                          </Avatar>
                          <Box sx={{ flex: 1, minWidth: 0, display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Typography
                                  variant="subtitle2"
                                  sx={{
                                    color: '#ffffff',
                                    fontWeight: 600,
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    whiteSpace: 'nowrap',
                                  }}
                                >
                                  {user.firstName} {user.lastName}
                                </Typography>
                                <Chip
                                  size="small"
                                  label={user.userGroupType === 'Company User' ? '🏢 Company' : '👑 Admin'}
                                  sx={{
                                    height: 18,
                                    fontSize: '0.65rem',
                                    fontWeight: 600,
                                    backgroundColor: user.userGroupType === 'Company User'
                                      ? 'rgba(34, 197, 94, 0.2)'
                                      : 'rgba(147, 51, 234, 0.2)',
                                    color: user.userGroupType === 'Company User'
                                      ? '#22c55e'
                                      : '#a855f7',
                                    border: user.userGroupType === 'Company User'
                                      ? '1px solid rgba(34, 197, 94, 0.3)'
                                      : '1px solid rgba(147, 51, 234, 0.3)',
                                  }}
                                />
                              </Box>
                              <Typography
                                variant="body2"
                                sx={{
                                color: 'rgba(255, 255, 255, 0.7)',
                                fontSize: '0.8rem',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                minWidth: 0,
                                flex: 1
                              }}
                            >
                              {user.email}
                            </Typography>
                            </Box>
                            <Chip
                              label={user.userType}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '0.7rem',
                                backgroundColor: 'rgba(59, 130, 246, 0.2)',
                                color: '#60a5fa',
                                border: '1px solid rgba(59, 130, 246, 0.3)',
                                flexShrink: 0
                              }}
                            />
                            {user.phone && (
                              <Typography
                                variant="caption"
                                sx={{
                                  color: 'rgba(255, 255, 255, 0.6)',
                                  fontSize: '0.6rem',
                                  flexShrink: 0
                                }}
                              >
                                {formatPhoneNumber(user.phone)}
                              </Typography>
                            )}
                          </Box>
                          {selectedUser?.id === user.id && (
                            <PersonIcon sx={{ color: '#60a5fa', fontSize: '1.5rem' }} />
                          )}
                        </Box>
                      </CardContent>
                    </Card>
                  ))}
                  {users.length === 0 && (
                    <Box sx={{ textAlign: 'center', py: 4 }}>
                      <Typography
                        variant="body2"
                        sx={{ color: 'rgba(255, 255, 255, 0.6)' }}
                      >
                        No active users found
                      </Typography>
                    </Box>
                  )}
                </Box>
              )}

              <Typography
                variant="caption"
                display="block"
                sx={{
                  mt: 2,
                  textAlign: 'center',
                  color: 'rgba(255, 255, 255, 0.5)',
                  fontSize: '0.75rem',
                }}
              >
                👑 Admin users see all data. 🏢 Company users must select their company.
              </Typography>
              <Typography
                variant="caption"
                display="block"
                sx={{
                  textAlign: 'center',
                  color: 'rgba(34, 197, 94, 0.8)',
                  fontSize: '0.75rem',
                  fontWeight: 600,
                }}
              >
                💡 To test company selection, choose "Scott Stevenson"
              </Typography>

              {/* Client Company Selector Status */}
              <Card sx={{
                mt: 2,
                background: 'linear-gradient(135deg, rgba(59, 130, 246, 0.1), rgba(99, 102, 241, 0.08))',
                border: '1px solid rgba(59, 130, 246, 0.3)',
                borderRadius: '12px'
              }}>
                <CardContent sx={{ py: 2 }}>
                  <Typography variant="subtitle2" sx={{
                    color: '#60a5fa',
                    fontWeight: 700,
                    mb: 1.5,
                    display: 'flex',
                    alignItems: 'center',
                    gap: 1
                  }}>
                    🏢 Client Company Selector
                  </Typography>

                  <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1, fontSize: '0.8rem' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, px: 1, backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: '6px' }}>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        Selected User:
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#fff', fontWeight: 600 }}>
                        {selectedUser ? `${selectedUser.firstName} ${selectedUser.lastName}` : 'None'}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, px: 1, backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: '6px' }}>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        User Type:
                      </Typography>
                      <Typography variant="caption" sx={{ color: selectedUser?.userGroupType === 'Company User' ? '#22c55e' : '#a855f7', fontWeight: 600 }}>
                        {selectedUser?.userGroupType || 'Not Set'}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, px: 1, backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: '6px' }}>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        Company Selection:
                      </Typography>
                      <Typography variant="caption" sx={{ color: showCompanySelection ? '#22c55e' : '#ef4444', fontWeight: 600 }}>
                        {showCompanySelection ? 'ACTIVE' : 'INACTIVE'}
                      </Typography>
                    </Box>

                    <Box sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, px: 1, backgroundColor: 'rgba(255, 255, 255, 0.05)', borderRadius: '6px' }}>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        Companies Available:
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#fff', fontWeight: 600 }}>
                        {companies.length}
                      </Typography>
                    </Box>
                  </Box>

                  {selectedCompany && (
                    <Box sx={{
                      mt: 1,
                      py: 0.5,
                      px: 1,
                      backgroundColor: 'rgba(34, 197, 94, 0.1)',
                      borderRadius: '6px',
                      border: '1px solid rgba(34, 197, 94, 0.2)'
                    }}>
                      <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
                        Selected Company:
                      </Typography>
                      <Typography variant="caption" sx={{ color: '#22c55e', fontWeight: 600, ml: 0.5 }}>
                        {selectedCompany.name}
                      </Typography>
                    </Box>
                  )}
                </CardContent>
              </Card>
            </>
          )}

          {/* Company Selection Section - Only show for Company Users */}
          {!isRegistering && showCompanySelection && selectedUser && (
            <>
              <Divider
                sx={{
                  my: 3,
                  '&::before, &::after': {
                    borderColor: 'rgba(59, 130, 246, 0.2)',
                  },
                }}
              >
                <Typography
                  variant="body2"
                  sx={{
                    color: 'rgba(255, 255, 255, 0.6)',
                    fontWeight: 500,
                    px: 2,
                  }}
                >
                  Select Your Company
                </Typography>
              </Divider>

              {loadingCompanies ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                  <CircularProgress size={32} sx={{ color: '#60a5fa' }} />
                </Box>
              ) : (
                <>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5, maxHeight: '200px', overflowY: 'auto', mb: 2 }}>
                    {companies.map((company) => (
                      <Card
                        key={company.id}
                        sx={{
                          background: selectedCompany?.id === company.id
                            ? 'linear-gradient(135deg, rgba(34, 197, 94, 0.15), rgba(34, 197, 94, 0.1))'
                            : 'linear-gradient(135deg, rgba(30, 41, 59, 0.8), rgba(51, 65, 85, 0.6))',
                          border: selectedCompany?.id === company.id
                            ? '2px solid rgba(34, 197, 94, 0.5)'
                            : '1px solid rgba(59, 130, 246, 0.2)',
                          borderRadius: '12px',
                          cursor: 'pointer',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            border: '1px solid rgba(34, 197, 94, 0.4)',
                            transform: 'translateY(-1px)',
                            boxShadow: '0 4px 12px rgba(34, 197, 94, 0.15)',
                          },
                        }}
                        onClick={() => handleCompanySelect(company)}
                      >
                        <CardContent sx={{ py: 1, px: 2, '&:last-child': { pb: 1 } }}>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                            <Avatar
                              sx={{
                                width: 40,
                                height: 40,
                                background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                                fontSize: '1rem',
                                fontWeight: 600
                              }}
                            >
                              {company.name.charAt(0)}
                            </Avatar>
                            <Box sx={{ flex: 1, minWidth: 0 }}>
                              <Typography
                                variant="subtitle2"
                                sx={{
                                  color: '#ffffff',
                                  fontWeight: 600,
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {company.name}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: 'rgba(255, 255, 255, 0.7)',
                                  fontSize: '0.8rem',
                                  overflow: 'hidden',
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap',
                                }}
                              >
                                {company.industry} • {company.size}
                              </Typography>
                            </Box>
                            <Chip
                              label={company.status}
                              size="small"
                              sx={{
                                height: 20,
                                fontSize: '0.7rem',
                                backgroundColor: company.status === 'Active' ? 'rgba(34, 197, 94, 0.2)' : 'rgba(156, 163, 175, 0.2)',
                                color: company.status === 'Active' ? '#22c55e' : '#9ca3af',
                                border: `1px solid ${company.status === 'Active' ? 'rgba(34, 197, 94, 0.3)' : 'rgba(156, 163, 175, 0.3)'}`,
                                flexShrink: 0
                              }}
                            />
                          </Box>
                        </CardContent>
                      </Card>
                    ))}
                    {companies.length === 0 && (
                      <Box sx={{ textAlign: 'center', py: 4 }}>
                        <Typography
                          variant="body2"
                          sx={{ color: 'rgba(255, 255, 255, 0.6)' }}
                        >
                          No companies available
                        </Typography>
                      </Box>
                    )}
                  </Box>

                  {/* Company Passcode Field */}
                  {selectedCompany && (
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      name="companyPasscode"
                      label="Company Access Passcode"
                      type="password"
                      id="companyPasscode"
                      value={companyPasscode}
                      onChange={(e) => setCompanyPasscode(e.target.value)}
                      placeholder="Enter company passcode..."
                      className="professional-input"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          background: 'rgba(30, 41, 59, 0.6)',
                          border: '1px solid rgba(34, 197, 94, 0.3)',
                          borderRadius: '8px',
                          backdropFilter: 'blur(10px)',
                          transition: 'all 0.3s ease',
                          '& fieldset': {
                            border: 'none',
                          },
                          '&:hover': {
                            borderColor: 'rgba(34, 197, 94, 0.5)',
                            boxShadow: '0 4px 12px rgba(34, 197, 94, 0.1)',
                          },
                          '&.Mui-focused': {
                            borderColor: 'rgba(34, 197, 94, 0.8)',
                            boxShadow: '0 0 0 2px rgba(34, 197, 94, 0.2)',
                          },
                        },
                        '& .MuiInputLabel-root': {
                          color: 'rgba(255, 255, 255, 0.7)',
                          '&.Mui-focused': {
                            color: '#22c55e',
                          },
                        },
                        '& .MuiOutlinedInput-input': {
                          color: 'white',
                        },
                      }}
                    />
                  )}

                  <Typography
                    variant="caption"
                    display="block"
                    sx={{
                      mt: 2,
                      textAlign: 'center',
                      color: 'rgba(255, 255, 255, 0.5)',
                      fontSize: '0.75rem',
                    }}
                  >
                    {selectedCompany
                      ? `Enter the passcode for ${selectedCompany.name}`
                      : 'Select your company and enter the access passcode'
                    }
                  </Typography>
                </>
              )}
            </>
          )}

          {isRegistering && (
            <Typography variant="body2" sx={{ mt: 2, textAlign: 'center', color: 'text.secondary' }}>
              Registration is disabled. Contact your administrator to create an account.
            </Typography>
          )}
        </Paper>
      </Box>
    </Container>
  );
};

export default Login;